// Cloud-first stack store with localStorage cache. When the user is signed in
// (and not in local-only mode), all reads/writes flow through Supabase tables
// mystack_stacks + mystack_stack_items. localStorage mirrors the result for
// instant repaint and fallback.
//
// When signed out OR local-only mode is on, behavior is pure localStorage —
// the same as before.

import { supabase, isCloudMode, currentUserSync } from './supabase';

export type StackItem = {
  id: string;
  compound_id: string;
  dose_mg: number | null;
  dose_value: number | null;
  unit: string;
  timing: string;
  notes: string | null;
  source: string | null;
  brand: string | null;
  active: boolean;
  from_blend: string | null;
  created_at?: string; // ISO; set when item is created
};

export type UserStack = {
  id: string;
  name: string;
  goal: string | null;
  slot_id: string | null; // 'morning'/'midday'/'evening'/'bedtime' for the 4 defaults
  items: StackItem[];
};

const K_LIST   = 'mystack:stacks:v1';
const K_ACTIVE = 'mystack:stacks:active:v1';

function safeParse<T>(s: string | null, fallback: T): T {
  if (!s) return fallback;
  try { return JSON.parse(s) as T; } catch { return fallback; }
}

function writeLocal(list: UserStack[]): void {
  localStorage.setItem(K_LIST, JSON.stringify(list));
}

function readLocal(): UserStack[] {
  const list = safeParse<UserStack[]>(localStorage.getItem(K_LIST), []);
  return Array.isArray(list) ? list : [];
}

/* ---------- Cloud reads / writes ---------- */

async function fetchCloud(): Promise<UserStack[]> {
  const u = currentUserSync();
  if (!u) return readLocal();
  const { data: stacksData, error: e1 } = await supabase
    .from('mystack_stacks')
    .select('id, name, goal, slot_id')
    .eq('owner_id', u.id);
  if (e1 || !stacksData) {
    console.warn('stack_store fetch stacks failed', e1);
    return readLocal();
  }
  const ids = stacksData.map((s) => s.id);
  if (ids.length === 0) { writeLocal([]); return []; }
  const { data: itemsData, error: e2 } = await supabase
    .from('mystack_stack_items')
    .select('id, stack_id, compound_id, dose_mg, dose_value, unit, timing, notes, source, brand, active, from_blend, display_order, created_at')
    .in('stack_id', ids)
    .order('display_order', { ascending: true });
  if (e2) {
    console.warn('stack_store fetch items failed', e2);
    return readLocal();
  }
  const out: UserStack[] = stacksData.map((s) => ({
    id: s.id,
    name: s.name,
    goal: (s as any).goal ?? null,
    slot_id: (s as any).slot_id ?? null,
    items: (itemsData || []).filter((it) => it.stack_id === s.id).map((it) => ({
      id: it.id,
      compound_id: it.compound_id,
      dose_mg: it.dose_mg,
      dose_value: (it as any).dose_value ?? null,
      unit: (it as any).unit ?? 'mg',
      timing: it.timing ?? '',
      notes: it.notes,
      source: (it as any).source ?? null,
      brand: it.brand,
      active: (it as any).active ?? true,
      from_blend: (it as any).from_blend ?? null,
      created_at: (it as any).created_at ?? undefined,
    })),
  }));
  writeLocal(out);
  return out;
}

async function persistStack(s: UserStack): Promise<void> {
  const u = currentUserSync();
  if (!u) return;
  // Upsert the stack row.
  const { error: e1 } = await supabase.from('mystack_stacks').upsert({
    id: s.id, owner_id: u.id, name: s.name, goal: s.goal, slot_id: s.slot_id,
  });
  if (e1) { console.warn('upsert stack failed', e1); return; }
  // Replace its items: simplest correct path = delete all then re-insert.
  // (Optimization later: diff.)
  await supabase.from('mystack_stack_items').delete().eq('stack_id', s.id);
  if (s.items.length > 0) {
    const rows = s.items.map((it, i) => ({
      id: it.id, stack_id: s.id, compound_id: it.compound_id,
      dose_mg: it.dose_mg, dose_value: it.dose_value, unit: it.unit,
      timing: it.timing, notes: it.notes, source: it.source, brand: it.brand,
      active: it.active, from_blend: it.from_blend,
      display_order: i,
      created_at: it.created_at,
    }));
    const { error: e2 } = await supabase.from('mystack_stack_items').insert(rows);
    if (e2) console.warn('insert items failed', e2);
  }
}

async function deleteCloud(id: string): Promise<void> {
  const u = currentUserSync();
  if (!u) return;
  const { error } = await supabase.from('mystack_stacks').delete().eq('id', id).eq('owner_id', u.id);
  if (error) console.warn('delete stack failed', error);
}

/* ---------- Public API ---------- */

/** Read stacks — cloud if signed in, else localStorage. */
export async function loadStacksAsync(): Promise<UserStack[]> {
  if (await isCloudMode()) return await fetchCloud();
  return readLocal();
}

/** Synchronous read of localStorage cache. Use for instant paint; pair with loadStacksAsync to refresh. */
export function loadStacks(): UserStack[] { return readLocal(); }

export async function saveStacks(list: UserStack[]): Promise<void> {
  writeLocal(list);
  if (await isCloudMode()) {
    for (const s of list) { await persistStack(s); }
  }
}

export async function upsertStack(s: UserStack): Promise<void> {
  const all = readLocal();
  const i = all.findIndex((x) => x.id === s.id);
  if (i < 0) all.push(s); else all[i] = s;
  writeLocal(all);
  if (await isCloudMode()) await persistStack(s);
}

export async function deleteStack(id: string): Promise<void> {
  writeLocal(readLocal().filter((s) => s.id !== id));
  if (await isCloudMode()) await deleteCloud(id);
}

export function getActiveStackId(): string | null {
  return localStorage.getItem(K_ACTIVE);
}

export function setActiveStackId(id: string | null): void {
  if (id === null) localStorage.removeItem(K_ACTIVE);
  else localStorage.setItem(K_ACTIVE, id);
}

export function newStackId(): string {
  // Use a uuid-like local id; Supabase column is uuid so we generate one.
  return crypto.randomUUID();
}

export function newItemId(): string { return crypto.randomUUID(); }

/** Add a compound to the active stack (creating one if needed). Async to flush
 * the create+insert through Supabase when cloud mode is on. */
export async function addToActive(item: Omit<StackItem, 'id'>): Promise<UserStack> {
  let stacks = readLocal();
  let activeId = getActiveStackId();
  let active = stacks.find((s) => s.id === activeId);
  if (!active) {
    active = { id: newStackId(), name: 'My Stack', goal: null, slot_id: null, items: [] };
    stacks.push(active);
    setActiveStackId(active.id);
  }
  active.items.push({ id: newItemId(), created_at: new Date().toISOString(), ...item });
  writeLocal(stacks);
  if (await isCloudMode()) await persistStack(active);
  return active;
}

/** Upload every localStorage stack to Supabase (first-time-after-signin migration). */
export async function migrateLocalToCloud(): Promise<number> {
  const u = currentUserSync();
  if (!u) return 0;
  const local = readLocal();
  let n = 0;
  for (const s of local) { await persistStack(s); n++; }
  return n;
}
